import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Lock, Euro, Users, AlertTriangle, TrendingUp, Calendar } from "lucide-react";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";
import DataSourcePanel from "./DataSourcePanel";

type EditableField = 'billable_rate' | 'cost_plus_rate' | 'target_hours';

interface Employee {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    billable_rate: number | string | null;
    cost_plus_rate: number | string | null;
    auto_timecard_flag: boolean | null;
    target_hours: number | string | null;
    account_state: string | null;
    m365_account_enabled: boolean | null;   // live from Microsoft Graph (preferred)
    created_at: string | null;
    updated_at: string | null;
}

const EmployeeRates: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'review' | 'missing' | 'all'>('active');
    const [updatingEmployeeId, setUpdatingEmployeeId] = useState<number | null>(null);
    const [hasAccess, setHasAccess] = useState<boolean | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [editingRate, setEditingRate] = useState<{ id: number; field: EditableField } | null>(null);
    const [rateDraft, setRateDraft] = useState<string>('');
    const [savingRate, setSavingRate] = useState<boolean>(false);

    const beginEditRate = (employee: Employee, field: EditableField) => {
        const current = employee[field];
        const numeric = current === null || current === undefined || current === ''
            ? ''
            : (typeof current === 'string' ? current : String(current));
        setEditingRate({ id: employee.id, field });
        setRateDraft(numeric);
        setUpdateError(null);
    };

    const cancelEditRate = () => {
        setEditingRate(null);
        setRateDraft('');
    };

    const saveRate = async () => {
        if (!editingRate) return;
        const { id, field } = editingRate;
        const trimmed = rateDraft.trim();
        if (trimmed === '') {
            cancelEditRate();
            return;
        }
        const numeric = parseFloat(trimmed);
        const isHours = field === 'target_hours';
        const max = isHours ? 168 : 100000;
        if (Number.isNaN(numeric) || numeric < 0 || numeric > max) {
            setUpdateError(
                isHours
                    ? 'Target hours must be a number between 0 and 168.'
                    : 'Rate must be a number between 0 and 100000.'
            );
            return;
        }

        try {
            setSavingRate(true);
            setUpdateError(null);
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) return;

            const url = `${getApiBaseUrl()}/api/employee-rates/${id}/rates`;
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };
            const body = JSON.stringify({ [field]: numeric });

            const methods: Array<'PUT' | 'POST' | 'PATCH'> = ['PUT', 'POST', 'PATCH'];
            let response: Response | null = null;
            let lastTransportError: any = null;

            for (const method of methods) {
                try {
                    const attempt = await fetch(url, { method, headers, body });
                    response = attempt;
                    if (attempt.ok) break;
                    if (attempt.status !== 404 && attempt.status !== 405) break;
                } catch (transportErr) {
                    lastTransportError = transportErr;
                }
            }

            if (!response && lastTransportError) throw lastTransportError;
            if (!response) throw new Error('Failed to update rate (no response)');

            if (!response.ok) {
                let serverError = '';
                const json = await response.json().catch(() => null);
                if (json && typeof json === 'object') {
                    serverError = (json as any).error || (json as any).message || '';
                }
                throw new Error(serverError || `Failed to update rate (HTTP ${response.status})`);
            }

            const data = await response.json().catch(() => null);
            const newValue = data && typeof data === 'object' && (data as any)[field] !== undefined
                ? (data as any)[field]
                : numeric;
            const updatedAt = (data && (data as any).updated_at) || new Date().toISOString();

            setEmployees(prev => prev.map(emp => (
                emp.id === id
                    ? { ...emp, [field]: newValue, updated_at: updatedAt }
                    : emp
            )));
            cancelEditRate();
        } catch (err: any) {
            console.error('Error updating rate:', err);
            setUpdateError(err.message || 'Failed to update rate');
        } finally {
            setSavingRate(false);
        }
    };

    const updateLocalAccountState = async (employeeId: number, nextState: 'active' | 'inactive') => {
        try {
            setUpdatingEmployeeId(employeeId);
            setUpdateError(null);
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) return;

            const url = `${getApiBaseUrl()}/api/employee-rates/${employeeId}/account-state`;
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };
            const body = JSON.stringify({ account_state: nextState });

            // Some environments/proxies can block methods differently.
            // Try PUT first, then POST, then PATCH and continue across network failures.
            const methods: Array<'PUT' | 'POST' | 'PATCH'> = ['PUT', 'POST', 'PATCH'];
            let response: Response | null = null;
            let lastTransportError: any = null;

            for (const method of methods) {
                try {
                    const attempt = await fetch(url, { method, headers, body });
                    response = attempt;
                    if (attempt.ok) break;
                    if (attempt.status !== 404 && attempt.status !== 405) break;
                } catch (transportErr) {
                    lastTransportError = transportErr;
                }
            }

            if (!response && lastTransportError) {
                throw lastTransportError;
            }

            if (!response) {
                throw new Error('Failed to update account state (no response)');
            }

            if (!response.ok) {
                let serverError = '';
                const json = await response.json().catch(() => null);
                if (json && typeof json === 'object') {
                    serverError = (json as any).error || (json as any).message || '';
                }
                if (!serverError) {
                    const text = await response.text().catch(() => '');
                    serverError = text ? text.slice(0, 240) : '';
                }
                throw new Error(serverError || `Failed to update account state (HTTP ${response.status})`);
            }

            setEmployees(prev => prev.map(emp => (
                emp.id === employeeId
                    ? { ...emp, account_state: nextState, updated_at: new Date().toISOString() }
                    : emp
            )));
        } catch (err: any) {
            console.error('Error updating local account state:', err);
            setUpdateError(err.message || 'Failed to update account state');
        } finally {
            setUpdatingEmployeeId(null);
        }
    };

    useEffect(() => {
        const fetchEmployees = async () => {
            if (accounts.length === 0) {
                setHasAccess(false);
                return;
            }

            setLoading(true);
            try {
                const token = await acquireTokenSafe(instance, accounts);
                if (!token) { setLoading(false); return; }

                const response = await fetch(`${getApiBaseUrl()}/api/employee-rates`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.status === 403) {
                    setHasAccess(false);
                    return;
                }

                if (!response.ok) {
                    throw new Error('Failed to fetch employees');
                }

                const data = await response.json();
                setEmployees(data);
                setFilteredEmployees(data);
                setHasAccess(true);
            } catch (err: any) {
                console.error('Error fetching employees:', err);
                setError(err.message || 'Failed to load employees');
                setHasAccess(true);
            } finally {
                setLoading(false);
            }
        };

        fetchEmployees();
    }, [instance, accounts, setLoading]);

    useEffect(() => {
        let filtered = employees;

        if (statusFilter !== 'all') {
            filtered = filtered.filter(emp => {
                const localActive = (emp.account_state || 'active').toLowerCase() === 'active';
                const m365Active = emp.m365_account_enabled;

                if (statusFilter === 'review') {
                    // Local active but disabled in M365 → candidates to mark inactive in DB
                    return localActive && m365Active === false;
                }
                if (statusFilter === 'missing') {
                    // Local active but no matching M365 account found at all
                    return localActive && m365Active === null;
                }
                if (statusFilter === 'active') {
                    return localActive;
                }
                if (statusFilter === 'inactive') {
                    return !localActive;
                }
                return true;
            });
        }

        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(
                (emp) =>
                    emp.first_name?.toLowerCase().includes(searchLower) ||
                    emp.last_name?.toLowerCase().includes(searchLower) ||
                    emp.email?.toLowerCase().includes(searchLower) ||
                    String(emp.billable_rate || '').includes(searchLower) ||
                    String(emp.cost_plus_rate || '').includes(searchLower) ||
                    String(emp.target_hours || '').includes(searchLower)
            );
        }

        setFilteredEmployees(filtered);
    }, [search, statusFilter, employees]);

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '—';
        try {
            return new Date(dateStr).toLocaleDateString('en-IE');
        } catch {
            return '—';
        }
    };

    const formatRate = (rate: number | string | null) => {
        if (rate === null || rate === undefined) return '—';
        const numRate = typeof rate === 'string' ? parseFloat(rate) : rate;
        if (isNaN(numRate)) return '—';
        return `€${numRate.toFixed(2)}`;
    };

    const parseNum = (val: number | string | null): number => {
        if (val === null || val === undefined) return 0;
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? 0 : num;
    };

    const calcWeeklyRevenue = (emp: Employee): number => {
        const rate = parseNum(emp.billable_rate);
        const hours = parseNum(emp.target_hours);
        return rate * hours;
    };

    const calcYearlyRevenue = (emp: Employee): number => {
        return calcWeeklyRevenue(emp) * 52;
    };

    // Per-field config drives the inline editor and the read-only display.
    const editFieldConfig: Record<EditableField, { prefix: string; step: string; max: string; display: (row: Employee) => string }> = {
        billable_rate: { prefix: '€', step: '0.01', max: '100000', display: (row) => formatRate(row.billable_rate) },
        cost_plus_rate: { prefix: '€', step: '0.01', max: '100000', display: (row) => formatRate(row.cost_plus_rate) },
        target_hours:   { prefix: '',  step: '0.5',  max: '168',    display: (row) => { const h = parseNum(row.target_hours); return h > 0 ? h.toFixed(1) : '—'; } },
    };

    const renderEditableCell = (row: Employee, field: EditableField) => {
        const cfg = editFieldConfig[field];
        const isEditing = editingRate?.id === row.id && editingRate?.field === field;
        if (isEditing) {
            return (
                <div className="rate-edit-wrap">
                    {cfg.prefix && <span className="rate-edit-prefix">{cfg.prefix}</span>}
                    <input
                        autoFocus
                        type="number"
                        step={cfg.step}
                        min="0"
                        max={cfg.max}
                        className="rate-edit-input"
                        value={rateDraft}
                        disabled={savingRate}
                        onChange={(e) => setRateDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); saveRate(); }
                            else if (e.key === 'Escape') { e.preventDefault(); cancelEditRate(); }
                        }}
                        onBlur={() => { if (!savingRate) saveRate(); }}
                    />
                </div>
            );
        }
        return (
            <button
                type="button"
                className="rate-edit-btn"
                title="Click to edit"
                onClick={() => beginEditRate(row, field)}
            >
                <span className="rate-value">{cfg.display(row)}</span>
            </button>
        );
    };

    const formatCurrency = (amount: number): string => {
        if (amount === 0) return '—';
        if (amount >= 1000000) return `€${(amount / 1000000).toFixed(2)}M`;
        if (amount >= 1000) return `€${(amount / 1000).toFixed(1)}K`;
        return `€${amount.toFixed(0)}`;
    };

    const columns: TableColumn<Employee>[] = [
        {
            name: "Name",
            selector: (row) => `${row.last_name}, ${row.first_name}`,
            sortable: true,
            cell: (row) => (
                <div className="employee-cell">
                    <div className="employee-name">{row.first_name} {row.last_name}</div>
                    <div className="employee-email">{row.email}</div>
                </div>
            ),
            width: "220px"
        },
        {
            name: "M365 Status",
            selector: (row) => row.m365_account_enabled ?? -1,
            sortable: true,
            cell: (row) => {
                const localActive = (row.account_state || 'active').toLowerCase() === 'active';
                const m365Enabled = row.m365_account_enabled;

                if (m365Enabled === true) {
                    return <span className="timecard-badge active">Enabled</span>;
                }
                if (m365Enabled === false) {
                    if (localActive) {
                        return (
                            <span className="timecard-badge inactive" title="Active in database but disabled in M365 — candidate to mark inactive locally">
                                Disabled ⚠️
                            </span>
                        );
                    }
                    return <span className="timecard-badge inactive">Disabled</span>;
                }
                if (localActive) {
                    return (
                        <span className="timecard-badge inactive" title="Active in database but no matching account was found in M365">
                            Not found ⚠️
                        </span>
                    );
                }
                return <span style={{ color: 'var(--text-muted)' }}>Unknown</span>;
            },
            center: true,
            width: "120px"
        },
        {
            name: "Local Status",
            selector: (row) => row.account_state || 'active',
            sortable: true,
            cell: (row) => {
                const state = (row.account_state || 'active').toLowerCase();
                if (state === 'active') {
                    return <span className="timecard-badge active">Active</span>;
                }
                return <span className="timecard-badge inactive">Inactive</span>;
            },
            center: true,
            width: "100px"
        },
        {
            name: "State Action",
            cell: (row) => {
                const isActive = (row.account_state || 'active').toLowerCase() === 'active';
                const isBusy = updatingEmployeeId === row.id;
                const nextState = isActive ? 'inactive' : 'active';
                return (
                    <button
                        onClick={() => updateLocalAccountState(row.id, nextState)}
                        disabled={isBusy}
                        className="state-action-btn"
                        title={`Update local database account_state to ${nextState}`}
                    >
                        {isBusy ? 'Saving...' : (isActive ? 'Set inactive' : 'Set active')}
                    </button>
                );
            },
            center: true,
            width: "140px"
        },
        {
            name: "Billable Rate",
            selector: (row) => row.billable_rate || 0,
            sortable: true,
            cell: (row) => renderEditableCell(row, 'billable_rate'),
            right: true,
            width: "140px"
        },
        {
            name: "Cost Plus Rate",
            selector: (row) => row.cost_plus_rate || 0,
            sortable: true,
            cell: (row) => renderEditableCell(row, 'cost_plus_rate'),
            right: true,
            width: "150px"
        },
        {
            name: "Auto Timecard",
            selector: (row) => row.auto_timecard_flag ? 'Yes' : 'No',
            sortable: true,
            cell: (row) => (
                <span className={`timecard-badge ${row.auto_timecard_flag ? 'active' : 'inactive'}`}>
                    {row.auto_timecard_flag ? 'Yes' : 'No'}
                </span>
            ),
            center: true,
            width: "120px"
        },
        {
            name: "Target Hours",
            selector: (row) => parseNum(row.target_hours),
            sortable: true,
            cell: (row) => renderEditableCell(row, 'target_hours'),
            right: true,
            width: "120px"
        },
        {
            name: "Weekly Max",
            selector: (row) => calcWeeklyRevenue(row),
            sortable: true,
            cell: (row) => {
                const weekly = calcWeeklyRevenue(row);
                return <span className="revenue-value">{formatCurrency(weekly)}</span>;
            },
            right: true,
            width: "110px"
        },
        {
            name: "Yearly Max",
            selector: (row) => calcYearlyRevenue(row),
            sortable: true,
            cell: (row) => {
                const yearly = calcYearlyRevenue(row);
                return <span className="revenue-value yearly">{formatCurrency(yearly)}</span>;
            },
            right: true,
            width: "110px"
        },
        {
            name: "Last Updated",
            selector: (row) => row.updated_at || row.created_at || '',
            sortable: true,
            cell: (row) => formatDate(row.updated_at || row.created_at),
            width: "120px"
        }
    ];

    // KPI base set: respect the status filter (active/inactive/review/missing/all) but ignore the search box.
    // Active/inactive use local account_state. Review/missing cross-check local active users against M365.
    const kpiEmployees = React.useMemo(() => {
        if (statusFilter === 'all') return employees;
        return employees.filter(emp => {
            const localActive = (emp.account_state || 'active').toLowerCase() === 'active';
            const m365Active = emp.m365_account_enabled;

            if (statusFilter === 'review') {
                return localActive && m365Active === false;
            }
            if (statusFilter === 'missing') {
                return localActive && m365Active === null;
            }
            if (statusFilter === 'active') {
                return localActive;
            }
            if (statusFilter === 'inactive') {
                return !localActive;
            }
            return true;
        });
    }, [employees, statusFilter]);

    const employeesWithRates = kpiEmployees.filter(e => {
        const rate = typeof e.billable_rate === 'string' ? parseFloat(e.billable_rate) : e.billable_rate;
        return rate !== null && !isNaN(rate as number) && rate > 0;
    });
    const avgHourlyRate = employeesWithRates.length > 0
        ? employeesWithRates.reduce((sum, e) => {
            const rate = typeof e.billable_rate === 'string' ? parseFloat(e.billable_rate) : (e.billable_rate || 0);
            return sum + (typeof rate === 'number' ? rate : 0);
        }, 0) / employeesWithRates.length
        : 0;

    const totalWeeklyRevenue = kpiEmployees.reduce((sum, e) => sum + calcWeeklyRevenue(e), 0);
    const totalYearlyRevenue = kpiEmployees.reduce((sum, e) => sum + calcYearlyRevenue(e), 0);
    const totalTargetHours = kpiEmployees.reduce((sum, e) => sum + parseNum(e.target_hours), 0);

    if (hasAccess === false) {
        return (
            <div className="page-container">
                <div className="access-denied">
                    <Lock size={48} />
                    <h2>Access Restricted</h2>
                    <p>You don't have permission to view Employee Rates.</p>
                    <p>Please contact your administrator if you believe this is an error.</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <Euro size={28} />
                        <h1>Employee Rates</h1>
                    </div>
                </div>
                <div className="warning-banner">
                    <AlertTriangle size={20} />
                    <span>Error loading employees: {error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div className="page-title">
                    <Euro size={20} />
                    <h1>Employee Rates</h1>
                    <span className="restricted-badge">
                        <Lock size={12} />
                        Restricted
                    </span>
                </div>
            </div>

            <div className="stats-grid stats-grid-6">
                <div className="stat-card">
                    <div className="stat-icon">
                        <Users size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{kpiEmployees.length}</div>
                        <div className="stat-label">Employees</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <Users size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{employeesWithRates.length}</div>
                        <div className="stat-label">With Rates</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <Euro size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">€{avgHourlyRate.toFixed(0)}</div>
                        <div className="stat-label">Avg Rate/Hr</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <Calendar size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{totalTargetHours.toFixed(0)}</div>
                        <div className="stat-label">Total Hrs/Wk</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <TrendingUp size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalWeeklyRevenue)}</div>
                        <div className="stat-label">Weekly Max</div>
                    </div>
                </div>
                <div className="stat-card highlight">
                    <div className="stat-icon">
                        <TrendingUp size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{formatCurrency(totalYearlyRevenue)}</div>
                        <div className="stat-label">Yearly Max</div>
                    </div>
                </div>
            </div>

            <div className="warning-banner compact">
                <AlertTriangle size={16} />
                <span>Sensitive data — do not share without authorization</span>
            </div>

            {updateError && (
                <div className="warning-banner compact" style={{ marginTop: 12 }}>
                    <AlertTriangle size={16} />
                    <span>Could not save change: {updateError}</span>
                </div>
            )}

            <div className="table-controls">
                <input
                    type="text"
                    placeholder="Search employees..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="search-input"
                />
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'active' | 'inactive' | 'review' | 'missing' | 'all')}
                    className="search-input"
                    style={{ maxWidth: 240 }}
                    title="Filter by local account_state, plus a review mode for local active users disabled in M365"
                >
                    <option value="active">Active (local DB)</option>
                    <option value="inactive">Inactive (local DB)</option>
                    <option value="review">Review needed (local active, M365 disabled)</option>
                    <option value="missing">Review needed (local active, not found in M365)</option>
                    <option value="all">All ({employees.length})</option>
                </select>
            </div>

            <div className="data-table-container employee-rates-table">
                <DataTable
                    columns={columns}
                    data={filteredEmployees}
                    pagination
                    paginationPerPage={50}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    highlightOnHover
                    responsive
                    conditionalRowStyles={[
                        {
                            when: (row: Employee) => parseNum(row.billable_rate) === 0 || parseNum(row.cost_plus_rate) === 0,
                            classNames: ['missing-rates-row'],
                        },
                    ]}
                    noDataComponent={
                        <div className="no-data">No employees found</div>
                    }
                    customStyles={{
                        table: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                borderRadius: '8px',
                            },
                        },
                        headRow: {
                            style: {
                                backgroundColor: 'var(--bg-tertiary)',
                                borderBottomColor: 'var(--border-color)',
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                            },
                        },
                        headCells: {
                            style: {
                                color: 'var(--text-secondary)',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            },
                        },
                        rows: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                borderBottomColor: 'var(--border-color)',
                            },
                            highlightOnHoverStyle: {
                                backgroundColor: 'var(--bg-hover)',
                                color: 'var(--text-primary)',
                                borderBottomColor: 'var(--border-color)',
                            },
                        },
                        cells: {
                            style: {
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            },
                        },
                        pagination: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-secondary)',
                                borderTopColor: 'var(--border-color)',
                            },
                            pageButtonsStyle: {
                                color: 'var(--text-secondary)',
                                fill: 'var(--text-secondary)',
                                '&:hover:not(:disabled)': {
                                    backgroundColor: 'var(--bg-hover)',
                                },
                                '&:disabled': {
                                    opacity: 0.4,
                                },
                            },
                        },
                        noData: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-secondary)',
                            },
                        },
                    }}
                />
            </div>

            <DataSourcePanel
                summary={
                    <>
                        Employee identity comes from the MKP Postgres database (the
                        Kinore practice-management app). Account-enabled status is reconciled
                        live against Microsoft 365 via Microsoft Graph (the source of truth
                        for active employees). Billable and cost-plus rates are stored in the
                        analytics warehouse and edited inline from this page.
                    </>
                }
                sections={[
                    {
                        title: "APIs called",
                        items: [
                            {
                                name: "GET /api/employee-rates",
                                description: (
                                    <>
                                        Returns the merged employee list with rates, time-card
                                        flags and M365 reconciliation. Joins MKP{" "}
                                        <code>admin_users</code> with cached Microsoft Graph
                                        user state.
                                    </>
                                ),
                            },
                            {
                                name: "PUT /api/employee-rates/:employeeId/rates",
                                description: (
                                    <>
                                        Inline edit. Validates 0\u2013100,000 with up to 2 dp
                                        and updates <code>billable_rate</code> /{" "}
                                        <code>cost_plus_rate</code> on{" "}
                                        <code>admin_users</code>.
                                    </>
                                ),
                            },
                            {
                                name: "PATCH /api/employee-rates/:employeeId/state",
                                description: (
                                    <>
                                        Activates / deactivates the local{" "}
                                        <code>admin_users</code> row to match M365.
                                    </>
                                ),
                            },
                        ],
                    },
                    {
                        title: "Tables read (MKP Postgres)",
                        items: [
                            {
                                name: "admin_users",
                                description:
                                    "Employee identity, billable_rate, cost_plus_rate, target_hours, status.",
                            },
                            {
                                name: "time_cards",
                                description:
                                    "Used to flag employees with no recent submitted time.",
                            },
                        ],
                    },
                    {
                        title: "External sources",
                        items: [
                            {
                                name: "Microsoft Graph /users",
                                description: (
                                    <>
                                        Live <code>accountEnabled</code> status, cached for 10
                                        minutes. Drives the \u201cReview needed\u201d filter
                                        when local state and M365 disagree.
                                    </>
                                ),
                            },
                        ],
                    },
                ]}
            />

            <style>{`
                .employee-rates-table .employee-cell {
                    padding: 8px 0;
                }

                .employee-rates-table .employee-name {
                    font-weight: 500;
                    color: var(--text-primary);
                }

                .employee-rates-table .employee-email {
                    font-size: 0.75rem;
                    color: var(--text-muted);
                    margin-top: 2px;
                }

                .employee-rates-table .rate-value {
                    font-weight: 500;
                    color: var(--accent-color);
                }

                .employee-rates-table .revenue-value {
                    font-weight: 500;
                    color: var(--text-primary);
                }

                .employee-rates-table .revenue-value.yearly {
                    color: var(--accent-green-bright);
                }

                .employee-rates-table .timecard-badge {
                    font-size: 0.7rem;
                    padding: 3px 6px;
                    border-radius: 4px;
                }

                .employee-rates-table .state-action-btn {
                    border: 1px solid var(--border-color);
                    background: var(--bg-tertiary);
                    color: var(--text-primary);
                    border-radius: 6px;
                    padding: 4px 8px;
                    font-size: 0.72rem;
                    line-height: 1.2;
                    white-space: nowrap;
                    min-width: 92px;
                    cursor: pointer;
                }

                .employee-rates-table .state-action-btn:hover:not(:disabled) {
                    background: var(--bg-hover);
                }

                .employee-rates-table .state-action-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .timecard-badge.active { background: rgba(16, 185, 129, 0.15); color: #34d399; }
                .timecard-badge.inactive { background: rgba(107, 114, 128, 0.1); color: var(--text-muted); }

                .employee-rates-table .rate-edit-btn {
                    background: transparent;
                    border: 1px dashed transparent;
                    color: inherit;
                    padding: 2px 6px;
                    border-radius: 4px;
                    cursor: pointer;
                    font: inherit;
                }
                .employee-rates-table .rate-edit-btn:hover {
                    border-color: var(--border-color);
                    background: var(--bg-hover);
                }
                .employee-rates-table .rate-edit-wrap {
                    display: inline-flex;
                    align-items: center;
                    gap: 2px;
                    background: var(--bg-tertiary);
                    border: 1px solid var(--accent-color);
                    border-radius: 4px;
                    padding: 2px 6px;
                }
                .employee-rates-table .rate-edit-prefix {
                    color: var(--text-muted);
                    font-size: 0.85rem;
                }
                .employee-rates-table .rate-edit-input {
                    width: 70px;
                    background: transparent;
                    border: none;
                    outline: none;
                    color: var(--text-primary);
                    font: inherit;
                    text-align: right;
                    padding: 0;
                    -moz-appearance: textfield;
                }
                .employee-rates-table .rate-edit-input::-webkit-outer-spin-button,
                .employee-rates-table .rate-edit-input::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }

                .employee-rates-table .missing-rates-row,
                .employee-rates-table .missing-rates-row:hover {
                    background-color: rgba(239, 68, 68, 0.12) !important;
                    border-left: 3px solid #ef4444;
                }
            `}</style>
        </div>
    );
};

export default EmployeeRates;
